import { Sparkles, FileText, Youtube, Video, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catColors = {
  "Access Control": "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  "Radar Systems": "bg-[#00C853]/10 text-[#00C853] border-[#00C853]/20",
  "Video Solutions": "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
  "Drone Security": "bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20",
  Protocols: "bg-[#0A0A0A]/10 text-[#0A0A0A] border-[#0A0A0A]/20",
};

/**
 * Real-time AI-Assist Copilot panel. Renders the top 3 KB articles surfaced
 * by the live transcript matcher. Each card is clickable to open the PDF
 * (GridFS-streamed) or the YouTube/Vimeo video in a new tab.
 */
export default function SuggestedArticlesPanel({ suggestions, loading, active }) {
  if (!active) return null;

  return (
    <div
      className="mt-4 border-t border-[#E5E7EB] pt-4"
      data-testid="copilot-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 flex items-center justify-center bg-gradient-to-br from-[#002FA7] to-[#FFB800]">
          <Sparkles size={14} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#002FA7]">
            AI-Assist Copilot
          </p>
          <p className="text-[9px] text-[#9CA3AF] font-mono">
            {loading
              ? "Listening to the call..."
              : `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}`}
          </p>
        </div>
        {loading && (
          <span className="w-2 h-2 bg-[#002FA7] animate-pulse rounded-full" data-testid="copilot-loading-dot" />
        )}
      </div>

      {suggestions.length === 0 && !loading && (
        <p className="text-[10px] text-[#9CA3AF] text-center py-3" data-testid="copilot-empty">
          Suggestions will appear here as the customer speaks.
        </p>
      )}

      <div className="space-y-2">
        {suggestions.map((s, idx) => {
          const catColor = catColors[s.category] || "bg-gray-100 text-gray-600 border-gray-200";
          const hasPdf = !!s.pdf_file_id;
          const hasVideo = !!s.video_url;

          return (
            <div
              key={s.article_id}
              className="p-2.5 bg-gradient-to-r from-[#F8F9FA] to-white border border-[#E5E7EB] hover:border-[#002FA7]/40 transition-colors group"
              data-testid={`copilot-suggestion-${idx}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#0A0A0A] leading-tight">{s.title}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Badge variant="outline" className={`rounded-none text-[9px] font-bold ${catColor}`}>
                      {s.category}
                    </Badge>
                    <span className="text-[9px] text-[#9CA3AF] font-mono">match: {s.score}</span>
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-[#4B5563] leading-relaxed line-clamp-2 mb-2">
                {s.snippet}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                {hasPdf && (
                  <a
                    href={`${API}/kb/file/${s.pdf_file_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] font-bold tracking-[0.05em] uppercase text-[#002FA7] hover:underline"
                    data-testid={`copilot-pdf-${idx}`}
                  >
                    <FileText size={11} />
                    {s.pdf_filename || "PDF"}
                  </a>
                )}
                {hasVideo && s.video_type === "youtube" && (
                  <a
                    href={s.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] font-bold tracking-[0.05em] uppercase text-[#FF2A2A] hover:underline"
                    data-testid={`copilot-yt-${idx}`}
                  >
                    <Youtube size={11} /> YouTube
                  </a>
                )}
                {hasVideo && s.video_type === "vimeo" && (
                  <a
                    href={s.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] font-bold tracking-[0.05em] uppercase text-[#00C853] hover:underline"
                    data-testid={`copilot-vimeo-${idx}`}
                  >
                    <Video size={11} /> Vimeo
                  </a>
                )}
                {!hasPdf && !hasVideo && (
                  <span className="flex items-center gap-1 text-[10px] text-[#9CA3AF] font-bold tracking-[0.05em] uppercase">
                    <ExternalLink size={11} /> Text only
                  </span>
                )}
                {s.tags?.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-[9px] bg-[#002FA7]/5 text-[#002FA7] px-1.5 py-0.5 font-bold">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
